import { Inject, Injectable } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Meta, Title } from '@angular/platform-browser';
import { Product } from '../models/product.model';
import { getProductUnitPrice } from '../analytics/meta-pixel.helpers';
import { resolveMerchantId } from '../analytics/merchant-id.util';
import { isProductOutOfStock } from '../utils/product-stock.util';

export interface SeoInput {
  title: string;
  description: string;
  path: string;
  image?: string | null;
  type?: 'website' | 'product';
  noindex?: boolean;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[] | null;
}

const STOREFRONT_ORIGIN = 'https://zerotechstores.com';
const DEFAULT_DESCRIPTION =
  'ZeroTech Stores — أجهزة منزلية وإلكترونيات بضمان وخدمة معتمدة في مصر.';
const JSON_LD_ATTR = 'data-zt-jsonld';

@Injectable({ providedIn: 'root' })
export class SeoService {
  constructor(
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly title: Title,
    private readonly meta: Meta
  ) {}

  applyHome(): void {
    this.apply({
      title: 'ZeroTech Stores',
      description: DEFAULT_DESCRIPTION,
      path: '/',
      type: 'website',
      jsonLd: {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: 'ZeroTech Stores',
        url: STOREFRONT_ORIGIN,
      },
    });
  }

  applyProduct(product: Product): void {
    const path = `/product/${product.id}`;
    const price = getProductUnitPrice(product);
    const inStock = !isProductOutOfStock(product);
    this.apply({
      title: `${product.name} | ZeroTech Stores`,
      description: this.plainText(product.description) || `${product.name} — ${DEFAULT_DESCRIPTION}`,
      path,
      image: product.imageUrl,
      type: 'product',
      jsonLd: [
        {
          '@context': 'https://schema.org',
          '@type': 'Product',
          name: product.name,
          sku: resolveMerchantId(product),
          image: product.imageUrl ? [product.imageUrl] : undefined,
          brand: product.brand || 'ZeroTech',
          offers: {
            '@type': 'Offer',
            url: `${STOREFRONT_ORIGIN}${path}`,
            priceCurrency: 'EGP',
            price,
            availability: inStock
              ? 'https://schema.org/InStock'
              : 'https://schema.org/OutOfStock',
          },
        },
        {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: STOREFRONT_ORIGIN },
            {
              '@type': 'ListItem',
              position: 2,
              name: product.categoryName || 'Category',
              item: `${STOREFRONT_ORIGIN}/category/${product.categoryId}`,
            },
            {
              '@type': 'ListItem',
              position: 3,
              name: product.name,
              item: `${STOREFRONT_ORIGIN}${path}`,
            },
          ],
        },
      ],
    });
  }

  applyCategory(name: string, categoryId: number | null): void {
    const path = categoryId != null ? `/category/${categoryId}` : '/';
    this.apply({
      title: `${name} | ZeroTech Stores`,
      description: `${name} — ${DEFAULT_DESCRIPTION}`,
      path,
      jsonLd: {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name,
        url: `${STOREFRONT_ORIGIN}${path}`,
      },
    });
  }

  applyStaticPage(path: string, title: string, description?: string): void {
    this.apply({
      title,
      description: description || DEFAULT_DESCRIPTION,
      path,
    });
  }

  applyForUrl(url: string): void {
    const path = url.split('?')[0];
    if (this.isNoIndexPath(path)) {
      this.apply({
        title: 'ZeroTech Stores',
        description: DEFAULT_DESCRIPTION,
        path,
        noindex: true,
        jsonLd: null,
      });
      return;
    }

    if (path === '/' || path === '') {
      this.applyHome();
      return;
    }
    if (path.startsWith('/branches')) {
      this.applyStaticPage('/branches', 'فروع ZeroTech | ZeroTech Stores');
      return;
    }
    if (path.startsWith('/support')) {
      this.applyStaticPage('/support', 'الدعم | ZeroTech Stores');
      return;
    }
    if (path.startsWith('/return-and-exchange-policy')) {
      this.applyStaticPage(
        '/return-and-exchange-policy',
        'سياسة الاسترجاع والاستبدال | ZeroTech Stores'
      );
    }
  }

  private apply(input: SeoInput): void {
    this.title.setTitle(input.title);
    const canonical = this.canonicalUrl(input.path);
    this.meta.updateTag({ name: 'description', content: input.description });
    this.meta.updateTag({
      name: 'robots',
      content: input.noindex ? 'noindex, nofollow' : 'index, follow',
    });
    this.meta.updateTag({ property: 'og:title', content: input.title });
    this.meta.updateTag({ property: 'og:description', content: input.description });
    this.meta.updateTag({ property: 'og:url', content: canonical });
    this.meta.updateTag({ property: 'og:type', content: input.type === 'product' ? 'product' : 'website' });
    if (input.image) {
      this.meta.updateTag({ property: 'og:image', content: input.image });
    }
    this.setCanonical(canonical);
    this.setJsonLd(input.jsonLd ?? null);
  }

  private canonicalUrl(path: string): string {
    const normalized = path.startsWith('/') ? path : `/${path}`;
    if (normalized === '/') {
      return `${STOREFRONT_ORIGIN}/`;
    }
    return `${STOREFRONT_ORIGIN}${normalized.replace(/\/+$/, '')}`;
  }

  private setCanonical(href: string): void {
    let link = this.document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!link) {
      link = this.document.createElement('link');
      link.setAttribute('rel', 'canonical');
      this.document.head.appendChild(link);
    }
    link.setAttribute('href', href);
  }

  private setJsonLd(payload: Record<string, unknown> | Record<string, unknown>[] | null): void {
    this.document.head.querySelectorAll(`script[${JSON_LD_ATTR}]`).forEach((node) => node.remove());
    if (!payload) {
      return;
    }
    const script = this.document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute(JSON_LD_ATTR, 'true');
    script.text = JSON.stringify(payload);
    this.document.head.appendChild(script);
  }

  private isNoIndexPath(path: string): boolean {
    return (
      path.startsWith('/cart') ||
      path.startsWith('/checkout') ||
      path.startsWith('/account') ||
      path.startsWith('/search') ||
      path.startsWith('/wishlist') ||
      path.startsWith('/compare')
    );
  }

  private plainText(value?: string | null): string {
    if (!value) {
      return '';
    }
    return value.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 160);
  }
}
